import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import SearchBar from "@/components/SearchBar";
import { Button } from "@/components/ui/button";
import { ResponsiveStaticImage } from "@/components/ui/ResponsiveStaticImage";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { useTopAnnouncementVisible } from "@/hooks/use-top-announcement-visible";
import { cn } from "@/lib/utils";
import {
  Clock,
  Search,
  BadgeCheck,
  ShieldCheck,
  type LucideIcon,
} from "lucide-react";

interface WhyRakbCard {
  icon: LucideIcon;
  titleKey: string;
  subtitleKey: string;
}

const HERO_WHY_CARDS: WhyRakbCard[] = [
  {
    icon: Clock,
    titleKey: "whyRakb.cards.availability.title",
    subtitleKey: "whyRakb.cards.availability.subtitle",
  },
  {
    icon: Search,
    titleKey: "whyRakb.cards.pricing.title",
    subtitleKey: "whyRakb.cards.pricing.subtitle",
  },
  {
    icon: BadgeCheck,
    titleKey: "whyRakb.cards.verified.title",
    subtitleKey: "whyRakb.cards.verified.subtitle",
  },
  {
    icon: ShieldCheck,
    titleKey: "whyRakb.cards.payment.title",
    subtitleKey: "whyRakb.cards.payment.subtitle",
  },
];

const HeroPremium = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const topAnnouncementVisible = useTopAnnouncementVisible();
  const mobileHeaderPad = user
    ? topAnnouncementVisible
      ? "max-md:pt-[calc(4rem+2.5rem+env(safe-area-inset-top,0px)+0.75rem)]"
      : "max-md:pt-[calc(4rem+env(safe-area-inset-top,0px)+0.75rem)]"
    : topAnnouncementVisible
      ? "max-md:pt-[calc(3rem+2.5rem+env(safe-area-inset-top,0px)+0.75rem)]"
      : "max-md:pt-[calc(3rem+env(safe-area-inset-top,0px)+0.75rem)]";

  // Image principale premium - Cour marocaine avec voiture
  const heroImage = "/images/hero-premium.jpg";
  const heroWebp = "/images/hero-premium.webp";

  // Scroll vers la section Destinations
  const handleScrollToDestinations = () => {
    const destinationsSection = document.getElementById("popular-destinations");
    if (destinationsSection) {
      destinationsSection.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <section className="relative md:min-h-[100dvh] md:overflow-hidden">
      {/* Background — profondeur cinématique légère, lisibilité préservée */}
      <div className="absolute inset-0">
        <div className="relative h-full min-h-full w-full overflow-hidden md:min-h-[100dvh]">
          <div className="absolute inset-0 origin-center motion-safe:animate-hero-ken-burns motion-reduce:animate-none">
            <ResponsiveStaticImage
              src={heroImage}
              webpSrc={heroWebp}
              alt={t("hero.heroImageAlt")}
              title={t("hero.heroImageAlt")}
              className="h-full min-h-full w-full object-cover object-[center_54%] sm:object-[center_52%] md:min-h-[100dvh]"
              loading="eager"
              decoding="async"
              sizes="100vw"
              fetchPriority="high"
              width={1920}
              height={1080}
            />
          </div>
          <div className="absolute inset-0 bg-black/35" />
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-black/30 via-black/5 to-black/65" />
          <div
            className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_90%_50%_at_50%_0%,rgba(255,255,255,0.06),transparent_52%)]"
            aria-hidden
          />
          <div
            className="pointer-events-none absolute inset-0 bg-gradient-to-tr from-transparent via-transparent to-emerald-950/18"
            aria-hidden
          />
        </div>
      </div>

      {/* Contenu principal — mobile : titre + recherche sous le header, desktop plein écran */}
      <div className="relative z-10 flex flex-col md:min-h-[100dvh]">
        <div
          className={cn(
            "flex flex-col items-center justify-start px-3 pb-5 sm:flex-1 sm:justify-center sm:px-6 sm:pb-14 sm:pt-28 lg:px-8",
            mobileHeaderPad
          )}
        >
          <div className="mx-auto w-full max-w-5xl text-center">
            {/* Titre — compact sur mobile pour laisser place aux listings */}
            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              className="text-balance font-heading text-[1.35rem] font-semibold leading-[1.05] tracking-[-0.03em] text-white min-[390px]:text-[1.5rem] sm:text-5xl md:text-6xl lg:text-7xl [text-shadow:0_2px_28px_rgba(0,0,0,0.38),0_0_1px_rgba(0,0,0,0.45)]"
            >
              <span className="text-white">{t('hero.titlePart1')}</span>
              <br className="hidden sm:block" />
              <span className="text-white sm:ms-0 max-md:ms-1 [text-shadow:0_1px_4px_rgba(0,0,0,0.45)]">
                {t('hero.titlePart2')}
              </span>
            </motion.h1>

            {/* Sous-titre — masqué sur mobile (densité hero) */}
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.12, ease: [0.22, 1, 0.36, 1] }}
              className="mx-auto mt-2 max-w-2xl text-pretty text-[13px] font-normal leading-snug text-white/95 sm:mt-5 sm:text-lg md:text-xl [text-shadow:0_1px_12px_rgba(0,0,0,0.45),0_0_1px_rgba(0,0,0,0.35)]"
            >
              {t('hero.subtitle')}
            </motion.p>

            {/* Barre de recherche — focus principal */}
            <motion.div
              id="home-hero-search"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.22, ease: [0.22, 1, 0.36, 1] }}
              className="mt-3 scroll-mt-36 sm:mt-8 sm:scroll-mt-32"
            >
              <SearchBar mobileDense />
            </motion.div>

            {/* CTAs — desktop only; mobile goes straight to listings */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="mt-4 hidden flex-col items-center justify-center gap-2 sm:mt-7 sm:flex sm:flex-row sm:gap-3"
            >
              <Button
                onClick={() => navigate("/search")}
                size="lg"
                className="h-11 w-full min-w-0 rounded-full border-0 bg-green-night px-5 text-sm font-semibold text-white shadow-lg shadow-black/20 transition-colors hover:bg-primary-hover sm:w-auto sm:min-w-[200px] sm:px-6"
              >
                {t('hero.ctaAll')}
              </Button>
              <Button
                onClick={handleScrollToDestinations}
                variant="outline"
                size="lg"
                className="h-11 w-full min-w-0 rounded-full border border-white/45 bg-white/10 px-5 text-sm font-medium text-white backdrop-blur-sm transition-colors hover:border-white/65 hover:bg-white/20 sm:w-auto sm:min-w-[200px] sm:px-6"
              >
                {t('hero.ctaCan')}
              </Button>
            </motion.div>
          </div>
        </div>

        {/* Pourquoi RAKB — desktop only (mobile: listings above the fold) */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="hidden px-4 pb-4 pt-2 sm:block sm:px-6 sm:pb-10 sm:pt-8 lg:px-8"
          id="why-rakb"
          aria-labelledby="why-rakb-title"
        >
          <div className="mx-auto max-w-5xl rounded-[1.5rem] border border-white/20 bg-black/20 px-3 py-4 shadow-[0_18px_50px_rgba(0,0,0,0.18)] backdrop-blur-md sm:rounded-[1.75rem] sm:px-7 sm:py-6">
            <h2
              id="why-rakb-title"
              className="mb-3 text-center font-heading text-[10px] font-semibold uppercase tracking-[0.2em] text-white/85 sm:mb-6 sm:text-xs"
            >
              {t("whyRakb.title")}
            </h2>
            <div className="flex snap-x snap-mandatory gap-1.5 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden min-[480px]:grid min-[480px]:grid-cols-4 min-[480px]:overflow-visible min-[480px]:pb-0 sm:gap-3 lg:gap-2">
              {HERO_WHY_CARDS.map((card, index) => {
                const Icon = card.icon;
                return (
                  <article
                    key={index}
                    className="group flex min-h-[5.75rem] min-w-[8.25rem] snap-start flex-col items-center justify-center gap-1.5 rounded-xl px-2 py-2 text-center transition-colors duration-200 hover:bg-white/10 min-[480px]:min-w-0 sm:min-h-36 sm:gap-2 sm:rounded-2xl sm:px-4 sm:py-4"
                  >
                    <div
                      className="flex h-8 w-8 items-center justify-center rounded-full bg-white/[0.12] text-white ring-1 ring-white/15 sm:h-10 sm:w-10"
                      aria-hidden
                    >
                      <Icon className="h-4 w-4 sm:h-5 sm:w-5" strokeWidth={1.65} />
                    </div>
                    <p className="max-w-[11rem] font-heading text-xs font-semibold leading-snug tracking-tight text-white sm:text-[15px]">
                      {t(card.titleKey)}
                    </p>
                    <p className="max-w-[10rem] text-[10px] leading-snug text-white/75 sm:text-[13px] sm:leading-relaxed">
                      {t(card.subtitleKey)}
                    </p>
                  </article>
                );
              })}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default HeroPremium;
